import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock } from 'lucide-react';
import ParticleField from '../components/ThreeScene/ParticleField';
import { useMousePosition } from '../hooks/useMousePosition';
import { fadeInUp, staggerContainer } from '../utils/animations';

const PrivacyPolicy = () => {
  const mousePosition = useMousePosition();

  return (
    <div className="relative min-h-screen bg-dark-surface">
      {/* 3D Canvas Background */}
      <div className="absolute inset-0 z-0">
        <Canvas
          camera={{ position: [0, 0, 15], fov: 60 }}
          gl={{ antialias: true, alpha: true }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.3} />
            <pointLight position={[10, 10, 10]} intensity={0.8} />
            <ParticleField
              count={1500}
              radius={12}
              colors={['#6C5CE7', '#1F51FF']}
              mousePosition={mousePosition}
            />
            <OrbitControls
              enableZoom={false}
              enablePan={false}
              autoRotate
              autoRotateSpeed={0.2}
              maxPolarAngle={Math.PI / 2}
              minPolarAngle={Math.PI / 2}
            />
          </Suspense>
        </Canvas>
      </div>
      {/* Hero Section */}
      <section className="relative z-10 py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-dark-surface/80 to-dark-surface-100/80">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Shield size={64} className="text-neon-purple mx-auto mb-6" />
            <h1 className="text-h1 font-bold mb-4">
              <span className="gradient-text">Privacy Policy</span>
            </h1>
            <p className="text-body-lg text-text-secondary max-w-2xl mx-auto">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="space-y-12"
          >
            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="text-neon-blue" size={24} />
                <h2 className="text-h3 font-bold">Information We Collect</h2>
              </div>
              <p className="text-text-secondary mb-4">
                We collect information you provide directly to us, such as when you create an account, 
                use our services, or contact us for support.
              </p>
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                <li>Personal information (name, email, phone number)</li>
                <li>Usage data and analytics</li>
                <li>Device and browser information</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="text-neon-purple" size={24} />
                <h2 className="text-h3 font-bold">How We Use Your Information</h2>
              </div>
              <p className="text-text-secondary mb-4">
                We use the information we collect to provide, maintain, and improve our services.
              </p>
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                <li>Provide and maintain our services</li>
                <li>Process transactions and send notifications</li>
                <li>Improve and personalize user experience</li>
                <li>Communicate with you about updates and offers</li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Data Protection</h2>
              <p className="text-text-secondary">
                We implement appropriate security measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. Your data 
                is encrypted and stored securely using industry-standard protocols.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Contact Us</h2>
              <p className="text-text-secondary">
                If you have any questions about this Privacy Policy, please contact us at 
                <span className="text-neon-purple"> privacy@starztech.com</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-neon-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-neon-blue/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};

export default PrivacyPolicy;